import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { type FormEvent, useRef, useState } from 'react'

import { ApiError, apiFetch } from '@/lib/api'
import { motivosDaSenha } from '@/lib/politica-de-senha'
import { destinoDaGuarda, sessaoQuery } from '@/lib/sessao'

import { CampoDeSenha } from '@/components/gestor/senha/CampoDeSenha'
import { CamposDaSenhaNova, RegrasDaSenha } from '@/components/gestor/senha/CamposDaSenhaNova'
import { QuadroDoCodigo } from '@/components/gestor/senha/QuadroDoCodigo'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'

// Folga de sobra para o tempo que a pessoa leva guardando o código.
const UM_DIA = 24 * 60 * 60 * 1000
const VAZIO = 'Preencha os três campos.'
const REPETICAO = 'As senhas novas não são iguais.'
const FORA_DA_REGRA = 'A senha nova não cumpre todas as regras da lista.'
const REPETE_A_INICIAL = 'A senha nova tem de ser diferente da inicial.'
const INICIAL_ERRADA = 'A senha inicial não confere.'
const FALHA = 'Não foi possível trocar a senha agora. Tente de novo em instantes.'

type Campo = 'inicial' | 'nova' | 'repeticao'
interface Recusa {
  texto: string
  campo: Campo
}

/**
 * A recusa da API (422) em texto de tela. Vários motivos podem vir juntos; vale
 * o que a pessoa corrige primeiro: a senha inicial, depois a igualdade com a
 * inicial, a repetição e, por último, a composição (a lista de regras já mostra
 * o que falta).
 */
function recusaDaApi(corpo: unknown): Recusa {
  const motivos =
    typeof corpo === 'object' &&
    corpo !== null &&
    'motivos' in corpo &&
    Array.isArray(corpo.motivos)
      ? (corpo.motivos as unknown[])
      : []
  if (motivos.includes('senha-atual')) return { texto: INICIAL_ERRADA, campo: 'inicial' }
  if (motivos.includes('repete-a-inicial')) return { texto: REPETE_A_INICIAL, campo: 'nova' }
  if (motivos.includes('repeticao')) return { texto: REPETICAO, campo: 'repeticao' }
  if (motivos.length > 0) return { texto: FORA_DA_REGRA, campo: 'nova' }
  return { texto: FALHA, campo: 'inicial' }
}

/*
 * `/gestor/senha`: a troca da senha inicial, a primeira coisa que a pessoa faz
 * depois de entrar. Enquanto a troca está pendente, a guarda da rota-mãe só
 * deixa esta tela abrir (fora da casca: não há menu para quem ainda não pode
 * usar as abas).
 *
 * As cinco regras aparecem marcadas ao digitar. A marca tem texto ("atendida" /
 * "pendente") além da cor, e a lista é uma região `aria-live` para a leitura de
 * tela avisar o que mudou. Quem decide é a API; a conferência aqui só poupa o
 * pedido: campo vazio, repetição diferente e composição incompleta não saem do
 * navegador. A igualdade com a senha inicial é conferida só pela API, que é
 * quem conhece a inicial.
 *
 * Cada um dos três campos tem o próprio olho. Durante "Trocando…" só os olhos
 * ficam desabilitados: os campos seguem habilitados, e o foco de cada recusa
 * continua indo ao campo do motivo.
 */
function TrocaDaSenha() {
  const queryClient = useQueryClient()
  const router = useRouter()
  const [inicial, setInicial] = useState('')
  const [nova, setNova] = useState('')
  const [repeticao, setRepeticao] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  // O código de recuperação do administrador: mostrado uma vez, só na memória desta tela.
  const [codigo, setCodigo] = useState<string | null>(null)
  const campoInicial = useRef<HTMLInputElement>(null)
  const campoNova = useRef<HTMLInputElement>(null)
  const campoRepeticao = useRef<HTMLInputElement>(null)

  const faltas = motivosDaSenha(nova)

  function recusar({ texto, campo }: Recusa) {
    setErro(texto)
    const alvo = { inicial: campoInicial, nova: campoNova, repeticao: campoRepeticao }[campo]
    alvo.current?.focus()
  }

  async function concluir() {
    // A sessão mudou no servidor (a troca deixa de estar pendente e o id foi
    // renovado): relê e vai para onde o cargo abre.
    await queryClient.invalidateQueries({ queryKey: sessaoQuery.queryKey })
    const sessao = queryClient.getQueryData(sessaoQuery.queryKey)
    router.history.replace(destinoDaGuarda(sessao, '/gestor/senha') ?? '/gestor')
  }

  /*
   * O servidor já tirou a sessão de "troca pendente", e o cache ainda guarda a
   * versão antiga. Se o cache ficar velho (1 minuto) e a janela recuperar o
   * foco, o TanStack Query relê a sessão e a guarda leva a pessoa ao painel,
   * levando junto o código que aparece só uma vez. Datar o cache no futuro o
   * mantém "fresco" para qualquer observador até o "Continuar", cuja
   * `invalidateQueries` relê a sessão de qualquer jeito.
   */
  function segurarASessao() {
    const sessao = queryClient.getQueryData(sessaoQuery.queryKey)
    if (sessao === undefined) return
    queryClient.setQueryData(sessaoQuery.queryKey, sessao, { updatedAt: Date.now() + UM_DIA })
    void queryClient.cancelQueries({ queryKey: sessaoQuery.queryKey })
  }

  const envio = useMutation({
    mutationFn: async () => {
      const resposta = await apiFetch<{ codigoDeRecuperacao?: string } | undefined>(
        '/v1/auth/senha-inicial',
        { method: 'POST', body: { senhaAtual: inicial, senhaNova: nova, repeticao } }
      )
      return resposta?.codigoDeRecuperacao ?? null
    },
    onSuccess: async (codigoNovo) => {
      // O administrador recebe o código na resposta: ele aparece antes de a sessão ser relida,
      // porque reler a sessão tira a pessoa desta tela.
      if (codigoNovo === null) return concluir()
      // As senhas não precisam ficar na memória enquanto o quadro está na tela.
      setInicial('')
      setNova('')
      setRepeticao('')
      segurarASessao()
      setCodigo(codigoNovo)
    },
    onError: (falha) => {
      if (falha instanceof ApiError && falha.status === 422) {
        recusar(recusaDaApi(falha.corpo))
      } else {
        setErro(FALHA)
      }
    },
  })

  function aoEnviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    if (envio.isPending) return
    if (inicial === '' || nova === '' || repeticao === '') {
      recusar({
        texto: VAZIO,
        campo: inicial === '' ? 'inicial' : nova === '' ? 'nova' : 'repeticao',
      })
      return
    }
    if (faltas.length > 0) return recusar({ texto: FORA_DA_REGRA, campo: 'nova' })
    if (nova !== repeticao) return recusar({ texto: REPETICAO, campo: 'repeticao' })
    setErro(null)
    envio.mutate()
  }

  const invalido = erro !== null
  const ligacao = invalido ? 'erro-troca' : undefined

  return (
    <div className="flex flex-1 flex-col bg-page p-4 sm:p-(--gef-gestor-conteudo-recuo)">
      <section
        aria-labelledby="titulo-troca"
        className="w-full max-w-(--gef-gestor-login-largura) rounded-lg bg-card p-(--gef-gestor-card-recuo) shadow-(--gef-gestor-card-sombra) sm:p-6"
      >
        <p className="m-0 text-xs font-bold tracking-wide text-primary uppercase">Área do Gestor</p>
        <h1 id="titulo-troca" className="mt-1 mb-0 text-3xl font-bold text-foreground">
          {codigo === null ? 'Troque a senha inicial' : 'Senha trocada'}
        </h1>
        {codigo !== null ? (
          <div className="mt-4">
            <QuadroDoCodigo codigo={codigo} onContinuar={() => void concluir()} />
          </div>
        ) : (
          <>
            <p className="mt-2 mb-0 text-base text-muted-foreground">
              É o primeiro acesso. Defina uma senha só sua para abrir o painel de gestão.
            </p>

            <form noValidate onSubmit={aoEnviar} className="mt-4 flex flex-col gap-4">
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="flex min-w-0 flex-col gap-2">
                  <Label htmlFor="troca-inicial">Senha inicial</Label>
                  <CampoDeSenha
                    ref={campoInicial}
                    id="troca-inicial"
                    nomeDoOlho="Mostrar senha inicial"
                    olhoDesabilitado={envio.isPending}
                    autoComplete="current-password"
                    value={inicial}
                    onChange={(evento) => setInicial(evento.target.value)}
                    aria-invalid={invalido || undefined}
                    aria-describedby={ligacao}
                  />
                </div>
                <CamposDaSenhaNova
                  prefixo="troca"
                  nova={nova}
                  repeticao={repeticao}
                  onNova={setNova}
                  onRepeticao={setRepeticao}
                  invalido={invalido}
                  ligacao={ligacao}
                  refNova={campoNova}
                  refRepeticao={campoRepeticao}
                  olhoDesabilitado={envio.isPending}
                />
              </div>

              <RegrasDaSenha senha={nova} />

              {/* Fundo do cartão, e não o rosado de erro: o vermelho do texto sobre o rosado
              dá 4,4:1, abaixo dos 4,5:1 da WCAG AA. */}
              {erro !== null && (
                <p
                  id="erro-troca"
                  role="alert"
                  className="m-0 rounded-lg border border-destructive bg-card px-3 py-2 text-base font-semibold text-destructive"
                >
                  {erro}
                </p>
              )}

              <div>
                <Button type="submit" disabled={envio.isPending}>
                  {envio.isPending ? 'Trocando…' : 'Trocar a senha'}
                </Button>
              </div>
            </form>
          </>
        )}
      </section>
    </div>
  )
}

export const Route = createFileRoute('/gestor/senha')({ component: TrocaDaSenha })
